import { defineStore } from 'pinia';
import api from '@/services/api';

const configuracion_model = {
  Codigo: null,
  Descripcion: null,
  Valor: null,
  TipoComponente: null,
};

function obtenerValorPorClave(state, claveInterna) {
  return state.configuracionList?.find((c) => c.ClaveInterna === claveInterna)?.Valor;
}

function parseBooleano(valor) {
  const v = (valor ?? '').toString().trim().toLowerCase();
  return v === '1' || v === 'true';
}

function parseNumero(valor, { entero = false, min, porDefecto = 0 } = {}) {
  if (!valor) return porDefecto;
  const parsed = entero ? parseInt(valor, 10) : parseFloat(valor);
  if (Number.isNaN(parsed)) return porDefecto;
  if (min !== undefined && parsed < min) return porDefecto;
  return parsed;
}

function parseTexto(valor, porDefecto = '') {
  return (valor ?? '').toString().trim() || porDefecto;
}

export const useConfiguracionStore = defineStore('configuracionStore', {
  state: () => ({
    configuracionList: [],
    configuracionGrupos: [],
    configuracion: JSON.parse(JSON.stringify(configuracion_model)),
    identidadPublica: {
      visible: false,
      nombreCliente: '',
    },
    page: 1,
  }),
  getters: {
    aprobacionPorPasswordHabilitada: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'habilitar_aprobacion_contrasena'));
    },
    mostrarGestionPedidos: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'gestion_pedidos'));
    },
    habilitarCotizacionesMasivas: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'habilitar_cotizaciones_masivas'));
    },
    mostrarGestionAdaptadores: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'gestion_adaptadores'));
    },
    decimalesImportes: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'decimales_importes'), {
        entero: true,
        min: 0,
        porDefecto: 2,
      });
    },
    mostrarBotonesRotacion: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'habilitar_botones_rotar_imposicion'));
    },
    rotacionImposicion: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'rotacion_imposicion'), {
        entero: true,
        porDefecto: 0,
      });
    },
    nuevoMotorImposicion: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'habilitar_nuevo_motor_imposicion'));
    },
    habilitarNuevaGrillaCostos: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'habilitar_nueva_grilla_costos'));
    },
    mostrarIdentidadCliente: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'mostrar_identidad_cliente_sispro'));
    },
    nombreClienteSisPro: (state) => {
      return parseTexto(obtenerValorPorClave(state, 'nombre_cliente_sispro'));
    },
    mostrarValorUnidadVentaGrilla: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'mostrar_valor_unidad_venta_grilla'));
    },
    mostrarValorUnidadProductoGrilla: (state) => {
      return parseBooleano(obtenerValorPorClave(state, 'mostrar_valor_unidad_producto_grilla'));
    },
    sellado: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'sellado'), { porDefecto: 0 });
    },
    solapa: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'solapa'), { porDefecto: 0 });
    },
    rangoMaximoToleranciaEntrega: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'rango_maximo_tolerancia_entrega'), {
        porDefecto: 0,
      });
    },
    rangoMinimoToleranciaEntrega: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'rango_minimo_tolerancia_entrega'), {
        porDefecto: 0,
      });
    },
    cantidadBocasModulos: (state) => {
      return parseNumero(obtenerValorPorClave(state, 'cantidad_bocas_modulos'), {
        entero: true,
        min: 0,
      });
    },
    tipoItemMezcla: (state) => {
      return parseTexto(obtenerValorPorClave(state, 'tipo_item_mezcla'), null);
    },
    tipoItemComponente: (state) => {
      return parseTexto(obtenerValorPorClave(state, 'tipo_item_componente'), null);
    },
    categoriaItemMaterialResultante: (state) => {
      return parseTexto(obtenerValorPorClave(state, 'categoria_item_material_resultante'), null);
    },
    calculoMaterialExtrusion: (state) => {
      const valor = parseTexto(
        obtenerValorPorClave(state, 'calculo_material_extrusion'),
        '',
      ).toLowerCase();
      return valor === 'gramaje' ? 'gramaje' : 'espesor_densidad';
    },
    filtroGenericoMaterialDefecto: (state) => {
      const valor = parseTexto(
        obtenerValorPorClave(state, 'filtro_generico_material_defecto'),
        '',
      ).toUpperCase();
      return ['TODOS', 'GENERICOS', 'NO_GENERICOS'].includes(valor) ? valor : 'TODOS';
    },
    sentidoBobinadoConfiguracion: (state) => {
      const valor = parseTexto(
        obtenerValorPorClave(state, 'sentido_bobinado_configuracion'),
        '',
      ).toLowerCase();
      return valor === 'personalizada' ? 'personalizada' : 'sistema';
    },
  },
  actions: {
    async findIdentidadPublicaHTTP() {
      try {
        const result = await api.get('/configuracion-general/identidad-publica');

        this.identidadPublica = {
          visible: !!result?.data?.visible,
          nombreCliente: result?.data?.nombreCliente || '',
        };

        return this.identidadPublica;
      } catch (error) {
        console.error('Error en findIdentidadPublicaHTTP:', error);

        this.identidadPublica = {
          visible: false,
          nombreCliente: '',
        };

        return this.identidadPublica;
      }
    },
    async findAllConfiguracionesHTTP(params) {
      const result = await api.get('/configuracion-general', {
        params: { page: +this.page, ...params },
      });
      if (result && result.data) {
        this.configuracionList.splice(0, this.configuracionList.length, ...result.data);
        this.setPage(this.page + 1);
        return true;
      }
      return false;
    },
    async findConfiguracionByCodigoHTTP(codigo) {
      try {
        const result = await api.get(`/configuracion-general/${codigo}`);
        if (result && result.data) {
          this.configuracion = result.data;
        }
      } catch (error) {
        console.error('Error en findConfiguracionByCodigoHTTP:', error);
      }
    },
    async uploadConfigImageHTTP(codigo, file) {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/configuracion-general/${codigo}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    async uploadPublicFileHTTP({ subdir, file, signal } = {}) {
      const fd = new FormData();
      fd.append('file', file);

      const { data } = await api.post(
        `/configuracion-general/upload-file?subdir=${encodeURIComponent(subdir || '')}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, signal },
      );
      return data;
    },
    async updateConfiguracionesHTTP(formList, pendingImages = {}) {
      const codigos = Object.keys(pendingImages || {});
      for (const codigo of codigos) {
        const file = pendingImages[codigo];
        if (!file) continue;
        const { filename } = await this.uploadConfigImageHTTP(codigo, file);
        const idx = formList.findIndex((i) => String(i.Codigo) === String(codigo));
        if (idx >= 0) formList[idx].Valor = filename;
      }

      const body = formList.map((param) => {
        const tipo = param.TipoComponente?.toLowerCase();
        let valorStr;
        if (tipo === 'switch') valorStr = param.Valor ? '1' : '0';
        else valorStr = String(param.Valor ?? '');
        return { Codigo: param.Codigo, Valor: valorStr };
      });

      const response = await api.patch('/configuracion-general', body);
      return response;
    },
    syncValoresLocales(formList) {
      for (const param of formList) {
        const actual = this.configuracionList.find(
          (c) => String(c.Codigo) === String(param.Codigo),
        );
        if (!actual) continue;
        const tipo = param.TipoComponente?.toLowerCase();
        actual.Valor = tipo === 'switch' ? (param.Valor ? '1' : '0') : String(param.Valor ?? '');
      }
    },
    async validarClave({ claveIngresada, claveInterna }) {
      try {
        const payload = { claveIngresada };
        if (claveInterna) payload.claveInterna = claveInterna;
        const result = await api.post(`/configuracion-general/validar-clave`, payload);
        if (result && result.data) {
          return result;
        }
      } catch (error) {
        console.error('Error en validarClave:', error);
        return;
      }
    },
    async firstConfiguraciones(params) {
      this.setPage(0);
      this.emptyList();
      await this.findAllConfiguracionesHTTP(params);
    },
    setPage(page) {
      this.page = page;
    },
    emptyList() {
      this.configuracionList = [];
    },
    async findGruposHTTP() {
      const result = await api.get('/configuracion-general/grupos');
      if (result && result.data) {
        this.configuracionGrupos = result.data;
        const flat = [];
        for (const grupo of result.data) {
          for (const param of grupo.parametros || []) {
            flat.push({
              Codigo: param.codigo,
              Descripcion: param.descripcion,
              Valor: param.valor,
              TipoComponente: param.tipoComponente,
              ClaveInterna: param.claveInterna,
              ClaveInternaGrupo: grupo.claveInterna,
              Leyenda: param.leyenda,
              Orden: param.orden,
            });
          }
        }
        this.configuracionList.splice(0, this.configuracionList.length, ...flat);
      }
    },
    async firstGrupos() {
      this.configuracionGrupos = [];
      this.emptyList();
      await this.findGruposHTTP();
    },
  },
});
